import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { palette } from 'theme/palette';
import { Box, useMediaQuery, CircularProgress } from '@mui/material';
import {
  IconBuildingStore,
  IconCirclePlus,
  IconEye,
  IconEyeClosed,
  IconFlag,
} from '@tabler/icons-react';
import { PERMISSIONS } from '@globalConstants';
import {
  useSelectedCompanyDetails,
  useTabSelector,
  useAreasByCustomer,
  useIsAreasAdmin,
  useCreateArea,
  useURLparams,
  useCompanyMovements,
  usePermissions,
  useShowAreaFeature,
} from '@hooks';
import { MainMenuLayout, FullWidthContent } from '@layouts';
import { CardHeader, TabSelector, AreaCardDetail } from '@molecules';
import { AreaCreationModal, ClientMovementsTable } from '@organisms';
import { parseAmount } from '@utils';
import styles from './styles';

/**
 * Mi Saldo screen
 */
const MyBalance = () => {
  const { t } = useTranslation();
  const { data: client, mutate: mutateCompany } = useSelectedCompanyDetails();
  const balance = client?.balance ? parseAmount(client.balance) : '-';
  const [showBalance, setShowBalance] = useState(false);
  const { isAreasAdmin } = useIsAreasAdmin();
  const { setURLparams } = useURLparams();

  const responsive = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const {
    data: areas,
    isLoading: isAreasLoading,
    mutate,
  } = useAreasByCustomer({
    customerId: client?.id,
  });
  const { showAreaFeature } = useShowAreaFeature();
  const [addAreaModalOpen, setAddAreaModalOpen] = useState(false);
  const { isLoading: isCreateAreasLoading, createArea } = useCreateArea();
  const canCreateAreas = usePermissions([PERMISSIONS.createAreas]);

  const itemsSelector = useMemo(() => {
    if (!client || !areas?.length) return [];
    const formattedData =
      client && Object.keys(client).length > 0 && isAreasAdmin
        ? { ...client, balance: client.balanceAvailable ?? client.balance, isCompany: true }
        : null;
    return formattedData ? [formattedData, ...areas] : areas;
  }, [client, areas, isAreasAdmin]);

  const { selectedItem: selectedArea, setSelectedItem: setSelectedArea } = useTabSelector({
    data: itemsSelector,
    propKey: 'id',
  });

  const { params } = useURLparams();

  /**
   * Function to prevent additional custom params being overwritten
   */
  function handleNewSearchParams(newValues) {
    setURLparams({
      ...(params.tab && { tab: params.tab }),
      ...(params.customerId && { customerId: params.customerId }),
      ...newValues,
    });
  }

  const {
    filtersOpen,
    setFiltersOpen,
    isLoading: companyMovementsIsLoading,
    data: companyMovementsData,
    handlePageChange,
    handleRowsPerPageChange,
    onFiltersFormSubmit,
    mutate: companyMovementsMutate,
  } = useCompanyMovements(
    {
      params,
      setURLparams: handleNewSearchParams,
    },
    !selectedArea?.isCompany && selectedArea?.id
  );

  /** */
  const onChangeBalance = () => {
    setShowBalance(!showBalance);
  };

  const leftComponentOptions = {
    type: 'avatar',
    icon: <IconBuildingStore color={palette.primary.main} />,
  };
  const rightComponentOptions = {
    type: 'text',
    text: !showBalance ? balance : '$ *****',
    icon: !showBalance ? <IconEye /> : <IconEyeClosed />,
    onClickIcon: onChangeBalance,
  };

  const subtitleOptions = {
    text: client?.cuil || '-',
  };

  /**
   * Submits the creation of an area with the values given in the modal
   */
  const onSubmitAreaCreation = async (values) => {
    await createArea(values);
    mutate();
    setAddAreaModalOpen(false);
  };

  /**
   * add icon, title and onclick function to each area
   */
  const getParsedAreas = () => {
    const items = itemsSelector?.map((item) => {
      return {
        ...item,
        icon: item.isCompany ? <IconBuildingStore /> : <IconFlag />,
        title: (
          <Box style={styles.titleStyle}>
            {item.isCompany ? item.description : item.fantasyName}
            <b>{Boolean(item.balance) || item.balance === 0 ? parseAmount(item.balance) : ''}</b>
          </Box>
        ),
        onClick: () => {
          setURLparams({});
          setSelectedArea(item);
        },
      };
    });
    return items;
  };

  return (
    <MainMenuLayout>
      <FullWidthContent>
        {!client || isAreasLoading || (showAreaFeature && !selectedArea) ? (
          <Box sx={styles.spinner}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <CardHeader
              withMenu={false}
              title={client?.description}
              leftComponentOptions={leftComponentOptions}
              rightComponentOptions={(!showAreaFeature || isAreasAdmin) && rightComponentOptions}
              subtitleOptions={subtitleOptions}
            />
            {showAreaFeature && (
              <TabSelector
                selectedTab={selectedArea}
                items={getParsedAreas()}
                keyProp='id'
                titleProp='title'
                rightButton={
                  isAreasAdmin &&
                  canCreateAreas && {
                    icon: <IconCirclePlus />,
                    text: t('CLIENTS:CREATE_AREA:ADD'),
                    onClick: () => {
                      setAddAreaModalOpen(true);
                    },
                  }
                }
              />
            )}
            <Box sx={styles.containerAreaMovements(responsive)}>
              {showAreaFeature && (
                <AreaCardDetail
                  area={selectedArea}
                  fetchAreas={mutate}
                  fetchCompany={mutateCompany}
                  fetchMovements={companyMovementsMutate}
                />
              )}
              {client?.id && (
                <ClientMovementsTable
                  {...{
                    filtersOpen,
                    setFiltersOpen,
                    isLoading: companyMovementsIsLoading,
                    data: companyMovementsData,
                    handlePageChange,
                    handleRowsPerPageChange,
                    onFiltersFormSubmit,
                    client,
                    idArea: !selectedArea?.isCompany && selectedArea?.id,
                  }}
                />
              )}
            </Box>
          </>
        )}
        <AreaCreationModal
          isOpen={addAreaModalOpen}
          isLoading={isCreateAreasLoading}
          onSubmit={onSubmitAreaCreation}
          closeModal={() => setAddAreaModalOpen(false)}
        />
      </FullWidthContent>
    </MainMenuLayout>
  );
};

export default MyBalance;
